import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ClientAddressHistory,
  ClientAddressHistorySchema,
} from '../../models/client/address.model';
import {
  ClientPersonalDetails,
  ClientPersonalDetailsSchema,
} from '../../models/client/personal.model';
import { ClientStore } from '../../stores/client/client.store';
import { SmartrService } from '../smartr.service';

@Injectable({ providedIn: 'root' })
export class ClientService extends SmartrService {
  private readonly http = inject(HttpClient);
  private readonly clientStore = inject(ClientStore);

  /**
   * Gets personal details for a specific client
   * @param clientId - The client ID
   * @returns Observable emitting the client's personal details
   */
  getPersonalDetails(clientId: number): Observable<ClientPersonalDetails> {
    return this.http.get<ClientPersonalDetails>(this.getPersonalDetailsUrl(clientId)).pipe(
      this.verifyResponse(ClientPersonalDetailsSchema),
      map((response: ClientPersonalDetails) => response),
      tap((details) => {
        if (details) {
          this.clientStore.setPersonalDetails(details);
        }
      }),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  /**
   * Gets address history for a specific client
   * @param clientId - The client ID
   * @returns Observable emitting the client's address history array
   */
  getAddressHistory(clientId: number): Observable<ClientAddressHistory> {
    return this.http.get<ClientAddressHistory>(this.getAddressHistoryUrl(clientId)).pipe(
      this.verifyResponse(ClientAddressHistorySchema),
      map((response: ClientAddressHistory) => response),
      tap((history) => {
        if (history) {
          this.clientStore.setAddressHistory(history);
        }
      }),
      catchError((error: HttpErrorResponse) => throwError(() => error))
    );
  }

  private getPersonalDetailsUrl(clientId: number): string {
    return environment.client.personalDetails.replace('{{clientId}}', clientId.toString());
  }

  private getAddressHistoryUrl(clientId: number): string {
    return environment.client.addressHistory.replace('{{clientId}}', clientId.toString());
  }
}
